import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import { apiFetch } from '../api/client';

const resultOptions = [
  { label: 'All Results', value: '' },
  { label: 'Success', value: '0' },
  { label: 'No Campaign', value: '1' },
  { label: 'Msisdn blacklisted', value: '2' },
  { label: 'Error sending to delivery channel', value: '3' },
  { label: 'No campaign available', value: '9' },
  { label: 'Default Error', value: '99' }
];

const logTypeLabel = (type) => {
  if (type === 1 || type === '1') return 'Ads Request';
  return type || '-';
};

const resultLabel = (value) => {
  const map = {
    0: 'Success',
    1: 'No Campaign',
    2: 'Msisdn blacklisted',
    3: 'Error sending to delivery channel',
    9: 'No campaign available',
    99: 'Default Error'
  };
  return map[value] || map[Number(value)] || value || '-';
};

const resultColor = (value) => {
  const num = Number(value);
  if (num === 0) return 'success';
  if (num === 1 || num === 9) return 'warning';
  return 'error';
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const buildQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') {
      qs.append(key, val);
    }
  });
  return qs.toString();
};

const CampaignLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('createdDatetime');
  const [sortDir, setSortDir] = useState('desc');
  const [meta, setMeta] = useState({ totalItems: 0 });
  const [stations, setStations] = useState([]);
  const [sourceChannels, setSourceChannels] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const toLocalIsoDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayIso = useMemo(() => toLocalIsoDate(new Date()), []);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toLocalIsoDate(d);
  }, []);

  const [filters, setFilters] = useState({
    search: '',
    origin: '',
    destination: '',
    sourceChannelId: '',
    result: '',
    startDate: defaultStart,
    endDate: todayIso
  });

  useEffect(() => {
    const fetchStations = async () => {
      try {
        const data = await apiFetch('/api/stations');
        setStations(Array.isArray(data.data) ? data.data : []);
      } catch (err) {
        setStations([]);
      }
    };
    fetchStations();
  }, []);

  useEffect(() => {
    const fetchSourceChannels = async () => {
      try {
        const data = await apiFetch('/api/source-channels');
        setSourceChannels(Array.isArray(data.data) ? data.data : []);
      } catch (err) {
        setSourceChannels([]);
      }
    };
    fetchSourceChannels();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const query = buildQuery({
        page: page + 1,
        pageSize,
        search: filters.search || undefined,
        origin: filters.origin || undefined,
        destination: filters.destination || undefined,
        sourceChannelId: filters.sourceChannelId || undefined,
        result: filters.result || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy,
        sortDir
      });
      const data = await apiFetch(`/api/campaign-logs${query ? `?${query}` : ''}`);
      const items = Array.isArray(data.data) ? data.data : data.data?.items || [];
      setLogs(items);
      setMeta(data.meta || { totalItems: items.length });
    } catch (err) {
      setError(err.message || 'Failed to load campaign logs');
      setLogs([]);
      setMeta({ totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, pageSize, filters, sortBy, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [key]: event.target.value
    }));
    setPage(0);
  };

  const handleSort = (columnId) => {
    const isAsc = sortBy === columnId && sortDir === 'asc';
    setSortBy(columnId);
    setSortDir(isAsc ? 'desc' : 'asc');
  };

  const handleDownload = async () => {
    try {
      const query = buildQuery({
        page: page + 1,
        pageSize,
        search: filters.search || undefined,
        origin: filters.origin || undefined,
        destination: filters.destination || undefined,
        sourceChannelId: filters.sourceChannelId || undefined,
        result: filters.result || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy,
        sortDir
      });
      const resp = await apiFetch(`/api/campaign-logs/export${query ? `?${query}` : ''}`, {
        parseAs: 'blob'
      });
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'campaign-logs.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download excel');
    }
  };

  const handleOpenMenu = (event) => setMenuAnchor(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchor(null);

  const columns = [
    { id: 'createdDatetime', label: 'Time' },
    { id: 'logType', label: 'Log Type' },
    { id: 'msisdn', label: 'Msisdn' },
    { id: 'origin', label: 'Origin' },
    { id: 'destination', label: 'Destination' },
    { id: 'sourceChannel', label: 'Source Channel' },
    { id: 'partnerTransactionId', label: 'Partner Transaction ID' },
    { id: 'campaignId', label: 'Campaign ID' },
    { id: 'timespan', label: 'Timespan (ms)' },
    { id: 'result', label: 'Result' }
  ];

  const totalCount = meta?.totalItems ?? logs.length;

  const stationLabel = (code) => stations.find((st) => st.code === code)?.name || code || '-';
  const sourceChannelLabel = (id) =>
    sourceChannels.find((sc) => sc.id === id || sc.id === Number(id))?.name || id || '-';

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Campaign Logs</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Campaign Logs
          </Typography>
          <Typography color="text.secondary">
            Monitor delivery requests and routing decisions across campaigns.
          </Typography>
        </Box>
        <IconButton
          aria-label="campaign log actions"
          onClick={handleOpenMenu}
          sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleCloseMenu}
          PaperProps={{
            sx: {
              background: 'linear-gradient(90deg, #4f46e5 0%, #06b6d4 100%)',
              color: '#fff',
              '& .MuiMenuItem-root': {
                color: 'inherit',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.08)'
                }
              }
            }
          }}
        >
          <MenuItem
            onClick={() => {
              handleCloseMenu();
              handleDownload();
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            Download Excel
          </MenuItem>
        </Menu>
      </Box>

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.05)'
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange('startDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 220 } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange('endDate')}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', md: 220 } }}
            />
            <TextField
              value={filters.search}
              onChange={handleFilterChange('search')}
              placeholder="Filter by ID / msisdn / partner trx / campaign ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{
                flexGrow: 1,
                width: '100%',
                boxSizing: 'border-box',
                minWidth: 0
              }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Origin</InputLabel>
              <Select value={filters.origin} label="Origin" onChange={handleFilterChange('origin')}>
                <MenuItem value="">All Origins</MenuItem>
                {stations.map((st) => (
                  <MenuItem key={st.code} value={st.code}>
                    {st.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Destination</InputLabel>
              <Select
                value={filters.destination}
                label="Destination"
                onChange={handleFilterChange('destination')}
              >
                <MenuItem value="">All Destinations</MenuItem>
                {stations.map((st) => (
                  <MenuItem key={st.code} value={st.code}>
                    {st.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Source Channel</InputLabel>
              <Select
                value={filters.sourceChannelId}
                label="Source Channel"
                onChange={handleFilterChange('sourceChannelId')}
              >
                <MenuItem value="">All Source Channels</MenuItem>
                {sourceChannels.map((sc) => (
                  <MenuItem key={sc.id} value={sc.id}>
                    {sc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Result</InputLabel>
              <Select value={filters.result} label="Result" onChange={handleFilterChange('result')}>
                {resultOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            border: '1px solid rgba(148, 163, 184, 0.16)',
            borderRadius: 1,
            mt: 3,
            backgroundColor: 'background.paper',
            '&::-webkit-scrollbar': {
              height: 6
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(148, 163, 184, 0.35)',
              borderRadius: 999
            }
          }}
        >
          <TableContainer
            sx={{
              display: 'inline-block',
              minWidth: '100%',
              width: '100%'
            }}
          >
            <Table sx={{ minWidth: 960, borderCollapse: 'separate', borderSpacing: 0 }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={sortBy === column.id ? sortDir : false}
                    >
                      {column.sortable === false ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortDir : 'asc'}
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow
                    hover
                    key={log.uuid || `${log.id}-${log.partnerTransactionId || log.partner_transaction_id}`}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.02)'
                      }
                    }}
                  >
                    <TableCell>{formatDateTime(log.createdDatetime || log.created_datetime)}</TableCell>
                    <TableCell>{logTypeLabel(log.logType || log.log_type)}</TableCell>
                    <TableCell>{log.msisdn || '-'}</TableCell>
                    <TableCell>
                      {log.originName || log.origin_name || stationLabel(log.origin)}
                    </TableCell>
                    <TableCell>
                      {log.destinationName || log.destination_name || stationLabel(log.destination)}
                    </TableCell>
                    <TableCell>
                      {log.sourceChannelName || sourceChannelLabel(log.sourceChannelId || log.source_channel_id)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260, wordBreak: 'break-word' }}>
                      {log.partnerTransactionId || log.partner_transaction_id || '-'}
                    </TableCell>
                    <TableCell>{log.campaignId ?? log.campaign_id ?? '-'}</TableCell>
                    <TableCell>{log.timespan ?? '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={resultLabel(log.result)}
                        color={resultColor(log.result)}
                        variant="filled"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!logs.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          No logs found
                        </Typography>
                        <Typography color="text.secondary">
                          Adjust filters to see available records.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[5, 10, 25, 50]}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            const next = parseInt(event.target.value, 10);
            setPageSize(next);
            setPage(0);
          }}
          sx={{
            '& .MuiTablePagination-toolbar': {
              px: { xs: 0, sm: 2 }
            }
          }}
        />
      </Paper>
    </Stack>
  );
};

export default CampaignLogPage;
